import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { COR_PADRAO, CORES, LIMITE_NOME, doEspaco, recusar } from '@/conversas/etiquetas';
import {
  atualizarEtiqueta,
  criarEtiqueta,
  ouvirEtiquetas,
  removerEtiqueta,
} from '@/services/conversasService';

/**
 * Etiquetas de UM setor — vive dentro de Preferências (Recepção ou Vendas).
 *
 * Saiu de Configurações (23/ago): quem atende é quem descobre que falta uma
 * etiqueta, e pedir ao gestor para criá-la fazia a ferramenta não ser usada.
 *
 * O `espaco` é da ROTA, não de um seletor: dentro de Preferências da Recepção
 * toda etiqueta é da recepção. Um seletor ali ofereceria criar para vendas — e
 * as `firestore.rules` recusariam, produzindo um erro que a tela não deveria ter
 * deixado acontecer.
 *
 * Quem pode editar já foi decidido pela rota (`RequireAuth papeis={RECEPCAO}` /
 * `{VENDAS}`); as rules são a autoridade no servidor.
 */

/** Paleta como botões, mais um seletor livre para a cor da marca da clínica. */
function EscolhaDeCor({ valor, aoEscolher, desabilitado, id }) {
  return (
    <div className="paleta">
      {CORES.map((c) => (
        <button
          key={c.valor}
          type="button"
          className={valor === c.valor ? 'cor escolhida' : 'cor'}
          style={{ background: c.valor }}
          onClick={() => aoEscolher(c.valor)}
          disabled={desabilitado}
          title={c.nome}
          aria-label={c.nome}
          aria-pressed={valor === c.valor}
        />
      ))}
      {/* Cor livre ao lado da paleta: a paleta é atalho, não limite. */}
      <input
        type="color"
        className="cor-livre"
        value={valor}
        onChange={(e) => aoEscolher(e.target.value)}
        disabled={desabilitado}
        title="Outra cor"
        aria-label="Escolher outra cor"
        id={id}
      />
    </div>
  );
}

/** Formulário compartilhado por criar e editar. */
function Formulario({ dados, aoMudar, aoSalvar, aoCancelar, salvando, rotuloSalvar, sufixo }) {
  return (
    <div className="etiqueta-form">
      <div className="campo">
        <label htmlFor={`et-nome-${sufixo}`}>Nome</label>
        <input
          id={`et-nome-${sufixo}`}
          type="text"
          value={dados.nome}
          maxLength={LIMITE_NOME}
          onChange={(e) => aoMudar({ ...dados, nome: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') aoSalvar();
          }}
          placeholder="Ex.: Orçamento enviado"
          disabled={salvando}
        />
      </div>

      <div className="campo">
        <label htmlFor={`et-cor-${sufixo}`}>Cor</label>
        <EscolhaDeCor
          id={`et-cor-${sufixo}`}
          valor={dados.cor}
          aoEscolher={(cor) => aoMudar({ ...dados, cor })}
          desabilitado={salvando}
        />
      </div>

      <div className="item-acoes">
        <button
          type="button"
          className="btn-primario compacto"
          onClick={aoSalvar}
          disabled={salvando || !dados.nome.trim()}
        >
          {salvando ? 'Salvando…' : rotuloSalvar}
        </button>
        {aoCancelar && (
          <button type="button" className="btn-texto" onClick={aoCancelar} disabled={salvando}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}

export default function SecaoEtiquetas({ espaco }) {
  const { clinicaId } = useAuth();
  const VAZIO = { nome: '', cor: COR_PADRAO, espaco };

  const [etiquetas, setEtiquetas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const [nova, setNova] = useState(VAZIO);
  const [editando, setEditando] = useState(null);
  // Confirmação INLINE, não modal nem `window.confirm`: alerta nativo trava a
  // janela inteira, e um modal para apagar um chip é mais cerimônia que o ato
  // merece. Guarda só o id em confirmação.
  const [confirmando, setConfirmando] = useState(null);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirEtiquetas(
      clinicaId,
      (lista) => {
        // Filtra pelo setor E ordena alfabeticamente: a coleção vem por id, que
        // é opaco, e a lista pularia de lugar a cada etiqueta nova.
        setEtiquetas(
          doEspaco(lista, espaco).sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? '')),
        );
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ler etiquetas:', e);
        setErro('Não foi possível carregar as etiquetas.');
        setCarregando(false);
      },
    );
  }, [clinicaId, espaco]);

  async function salvar(dados, idAtual) {
    const problema = recusar(dados, etiquetas, idAtual);
    if (problema) {
      setErro(problema);
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      if (idAtual) await atualizarEtiqueta(clinicaId, idAtual, dados);
      else await criarEtiqueta(clinicaId, dados);
      setEditando(null);
      if (!idAtual) setNova(VAZIO);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar etiqueta:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Seu papel não pode alterar as etiquetas deste setor.'
          : (e?.message ?? 'Não foi possível salvar a etiqueta.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  async function remover(etiqueta) {
    setSalvando(true);
    setErro(null);
    try {
      await removerEtiqueta(clinicaId, etiqueta.id);
      setConfirmando(null);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao remover etiqueta:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Seu papel não pode remover etiquetas deste setor.'
          : 'Não foi possível remover a etiqueta.',
      );
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="secao">
      <header className="secao-barra">
        <div>
          <h2 className="secao-titulo">Etiquetas</h2>
          <p className="secao-sub">Marcam conversas no inbox deste setor.</p>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}

      <div className="cartao">
        {carregando && <p className="campo-ajuda">Carregando…</p>}

        {!carregando && etiquetas.length === 0 && (
          <p className="campo-ajuda">Nenhuma etiqueta ainda. Crie a primeira abaixo.</p>
        )}

        <ul className="etiquetas-lista">
          {etiquetas.map((e) =>
            editando?.id === e.id ? (
              <li key={e.id} className="etiqueta-linha editando">
                <Formulario
                  dados={editando}
                  aoMudar={setEditando}
                  aoSalvar={() => salvar(editando, e.id)}
                  aoCancelar={() => {
                    setEditando(null);
                    setErro(null);
                  }}
                  salvando={salvando}
                  rotuloSalvar="Salvar"
                  sufixo={e.id}
                />
              </li>
            ) : (
              <li key={e.id} className="etiqueta-linha">
                {/* O chip é desenhado como no inbox: é assim que ele vai
                    aparecer, e conferir aqui evita descobrir na conversa. */}
                <span
                  className="chip-etiqueta marcada"
                  style={{ background: e.cor, borderColor: e.cor }}
                >
                  {e.nome}
                </span>
                <div className="item-acoes">
                    {confirmando === e.id ? (
                      <>
                        {/* Dito na cara: recriar com o mesmo nome NÃO traz as
                            marcações de volta, porque o id é outro. */}
                        <span className="campo-ajuda">
                          Remover? As conversas param de exibi-la.
                        </span>
                        <button
                          type="button"
                          className="btn-texto perigo"
                          onClick={() => remover(e)}
                          disabled={salvando}
                        >
                          {salvando ? 'Removendo…' : 'Sim, remover'}
                        </button>
                        <button
                          type="button"
                          className="btn-texto"
                          onClick={() => setConfirmando(null)}
                          disabled={salvando}
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn-texto"
                          onClick={() => {
                            setErro(null);
                            setConfirmando(null);
                            setEditando({
                              id: e.id,
                              nome: e.nome ?? '',
                              cor: e.cor ?? COR_PADRAO,
                              // Sempre o espaço da ROTA: editar não move de setor.
                              espaco,
                            });
                          }}
                          disabled={salvando}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn-texto perigo"
                          onClick={() => {
                            setErro(null);
                            setEditando(null);
                            setConfirmando(e.id);
                          }}
                          disabled={salvando}
                        >
                          Remover
                        </button>
                    </>
                  )}
                </div>
              </li>
            ),
          )}
        </ul>
      </div>

      <div className="cartao">
        <h3 className="secao-titulo pequeno">Nova etiqueta</h3>
        <Formulario
          dados={nova}
          aoMudar={setNova}
          aoSalvar={() => salvar(nova, null)}
          salvando={salvando}
          rotuloSalvar="Adicionar"
          sufixo="nova"
        />
      </div>
    </section>
  );
}
